import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './DealsTab.css';
import { apiGet, apiPost, apiDelete, ApiError } from '../utils/api';

// Mock data for development (only used when mock mode is enabled)
const mockDealsData = [
  {
    id: '1',
    name: "Organic Bananas",
    store: "Kroger",
    salePrice: 0.99,
    regularPrice: 1.49,
    promoEndDate: "2026-06-30",
    category: "Produce",
    source: "Kroger API",
    isActive: true
  },
  {
    id: '2',
    name: "Whole Milk",
    store: "Kroger",
    salePrice: 2.99,
    regularPrice: 3.99,
    promoEndDate: "2026-07-05",
    category: "Dairy",
    source: "Kroger API",
    isActive: true
  },
  {
    id: '3',
    name: "Chicken Breast",
    store: "Kroger",
    salePrice: 5.99,
    regularPrice: 8.99,
    promoEndDate: "2026-07-10",
    category: "Meat",
    source: "Kroger API",
    isActive: true
  },
  {
    id: '4',
    name: "Whole Wheat Bread",
    store: "Kroger",
    salePrice: 2.49,
    regularPrice: 3.49,
    promoEndDate: "2026-07-01",
    category: "Bakery",
    source: "Kroger API",
    isActive: true
  },
  {
    id: '5',
    name: "Baby Spinach",
    store: "Kroger",
    salePrice: 3.99,
    regularPrice: 5.99,
    promoEndDate: "2026-07-03",
    category: "Produce",
    source: "Kroger API",
    isActive: true
  }
];

const DealsTab = ({ currentUser }) => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [selectionsByDealId, setSelectionsByDealId] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestMessage, setIngestMessage] = useState(null);
  const [savings, setSavings] = useState(null);
  const [storeFilter, setStoreFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [selectionError, setSelectionError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(new Set());

  useEffect(() => {
    const loadDeals = async () => {
      try {
        setLoading(true);
        setError(null);

        const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';
        if (useMockData) {
          setDeals(mockDealsData);
          setLoading(false);
          return;
        }

        const dealsData = await apiGet('/deals');
        setDeals(dealsData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        console.error('Error loading deals:', err);
      }
    };

    loadDeals();
  }, []);

  useEffect(() => {
    const loadSelections = async () => {
      if (!currentUser) {
        setSelectionsByDealId(new Map());
        setSavings(null);
        return;
      }

      try {
        const [selections, summary] = await Promise.all([
          apiGet('/user-selections', { auth: true }),
          apiGet('/user-selections/summary', { auth: true }),
        ]);
        setSelectionsByDealId(new Map(selections.map((s) => [s.dealId, s.id])));
        setSavings(summary);
      } catch (err) {
        console.error('Error loading selections:', err.message);
      }
    };

    loadSelections();
  }, [currentUser]);

  const handleDealSelect = async (dealId) => {
    if (!currentUser) {
      alert('Please log in to select deals.');
      return;
    }

    const newSelections = new Map(selectionsByDealId);

    try {
      if (newSelections.has(dealId)) {
        const selectionId = newSelections.get(dealId);
        await apiDelete(`/user-selections/${selectionId}`, { auth: true });
        newSelections.delete(dealId);
      } else {
        const created = await apiPost('/user-selections', { dealId: String(dealId) }, { auth: true });
        newSelections.set(dealId, created.id);
      }
      setSelectionsByDealId(newSelections);
      // refresh running savings total
      try {
        setSavings(await apiGet('/user-selections/summary', { auth: true }));
      } catch (err) {
        console.error('Error updating selection:', err.message);
        setError(err.message);
      }
    } catch (err) {
      console.error('Error updating selection:', err.message);
      setError(err.message);
    }
  };

  const handleAddToShoppingList = async (deal) => {
    if (!currentUser) {
      alert('Please log in to add items to your shopping list.');
      return;
    }

    const key = String(deal.id);
    setAddingToCart((prev) => new Set(prev).add(key));

    try {
      await apiPost('/shopping-list', { name: deal.name }, { auth: true });
      alert(`Added "${deal.name}" to your shopping list!`);
    } catch (err) {
      console.error('Error adding to shopping list:', err);
      alert(`Failed to add "${deal.name}" to your shopping list.`);
    } finally {
      setAddingToCart((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleSearchRecipes = (deal) => {
    navigate('/recipes', { state: { searchTerm: deal.name } });
  };

  useEffect(() => {
    if (error) return;

    const refresh = async () => {
      try {
        const [selections, summary] = await Promise.all([
          apiGet('/user-selections', { auth: true }),
          apiGet('/user-selections/summary', { auth: true }),
        ]);
        setSelectionsByDealId(new Map(selections.map((s) => [s.dealId, s.id])));
        setSavings(summary);
        window.dispatchEvent(new CustomEvent('deal-selections-changed'));
      } catch (err) {
        console.error('Error loading selections:', err.message);
      }
    };

    if (currentUser) {
      refresh();
    } else {
      setSelectionsByDealId(new Map());
      setSavings(null);
    }
  }, [currentUser, error]);

  const isSelected = (dealId) => selectionsByDealId.has(dealId);

  const stores = useMemo(
    () => [...new Set(deals.map((d) => d.store))].sort(),
    [deals]
  );
  const categories = useMemo(
    () => [...new Set(deals.map((d) => d.category))].sort(),
    [deals]
  );

  const discountPercent = (deal) =>
    deal.regularPrice > 0 ? (deal.regularPrice - deal.salePrice) / deal.regularPrice : 0;

  const visibleDeals = useMemo(() => {
    let result = deals;
    if (storeFilter !== 'all') result = result.filter((d) => d.store === storeFilter);
    if (categoryFilter !== 'all') result = result.filter((d) => d.category === categoryFilter);

    const sorted = [...result];
    switch (sortBy) {
      case 'discount-desc':
        sorted.sort((a, b) => discountPercent(b) - discountPercent(a));
        break;
      case 'discount-asc':
        sorted.sort((a, b) => discountPercent(a) - discountPercent(b));
        break;
      case 'price-asc':
        sorted.sort((a, b) => a.salePrice - b.salePrice);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.salePrice - a.salePrice);
        break;
      default:
        break;
    }
    return sorted;
  }, [deals, storeFilter, categoryFilter, sortBy]);

  const refreshDeals = async () => {
    setIsRefreshing(true);
    try {
      const dealsData = await apiGet('/deals');
      setDeals(dealsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const triggerIngestion = async () => {
    setIsIngesting(true);
    setIngestMessage(null);
    try {
      const result = await apiPost('/deals/ingest', undefined, { auth: true });
      setIngestMessage(
        `Ingestion complete: ${result.dealsFetched} fetched, ${result.dealsCreated} created, ${result.dealsUpdated} updated.`
      );
      await refreshDeals();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setIngestMessage("You don't have permission to trigger ingestion.");
      } else {
        setIngestMessage(`Failed to trigger ingestion: ${err.message}`);
      }
    } finally {
      setIsIngesting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading deals...</div>;
  }

  if (error) {
    return (
      <div className="deals-tab">
        <h2>Weekly Deals</h2>
        <p className="deals-description">
          Select deals that interest you. These will be used to generate recipe ideas.
        </p>
        <div className="error">Error loading deals: {error}</div>
        <button onClick={refreshDeals} className="btn btn-primary">
          {isRefreshing ? 'Refreshing...' : 'Refresh Deals'}
        </button>
      </div>
    );
  }

  return (
    <div className="deals-tab">
      <h2>Weekly Deals</h2>
      <p className="deals-description">
        Select deals that interest you. These will be used to generate recipe ideas.
      </p>

      {currentUser && savings && savings.selectionCount > 0 && (
        <div className="savings-banner">
          Your {savings.selectionCount} selected deal{savings.selectionCount === 1 ? '' : 's'} save you{' '}
          <strong>${savings.totalSavings.toFixed(2)}</strong>{' '}
          <span className="savings-sub">
            (${savings.totalSalePrice.toFixed(2)} vs ${savings.totalRegularPrice.toFixed(2)} regular)
          </span>
        </div>
      )}

      <div className="deals-controls">
        <button onClick={refreshDeals} className="btn btn-primary" disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing...' : 'Refresh Deals'}
        </button>
        {currentUser && (
          <button onClick={triggerIngestion} className="btn btn-secondary" disabled={isIngesting}>
            {isIngesting ? 'Triggering...' : 'Trigger Ingestion'}
          </button>
        )}
      </div>

      {ingestMessage && <p className="ingest-message">{ingestMessage}</p>}

      {deals.length > 0 && (
        <div className="deals-filters">
          <select
            className="form-input"
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            aria-label="Filter by store"
          >
            <option value="all">All Stores</option>
            {stores.map((store) => (
              <option key={store} value={store}>{store}</option>
            ))}
          </select>

          <select
            className="form-input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            className="form-input"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort deals"
          >
            <option value="default">Sort: Default</option>
            <option value="discount-desc">Discount: High to Low</option>
            <option value="discount-asc">Discount: Low to High</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      )}

      <div className="deals-grid">
        {visibleDeals.map((deal) => (
          <div
            key={deal.id}
            className={`deal-card ${isSelected(deal.id) ? 'selected' : ''}`}
            onClick={(event) => {
              if (event.target.closest('.deal-action')) {
                return;
              }
              handleDealSelect(deal.id);
            }}
          >
            <div className="deal-header">
              <h3>{deal.name}</h3>
              <span className="store">{deal.store}</span>
            </div>

            <div className="deal-details">
              <div className="price-info">
                <span className="sale-price">${deal.salePrice}</span>
                <span className="regular-price">${deal.regularPrice}</span>
              </div>

              <div className="discount-badge">
                Save ${(deal.regularPrice - deal.salePrice).toFixed(2)} ({Math.round(((deal.regularPrice - deal.salePrice) / deal.regularPrice) * 100)}% off)
              </div>

              <div className="promo-date">
                Ends: {new Date(deal.promoEndDate).toLocaleDateString()}
              </div>

              <div className="category">
                {deal.category}
              </div>
            </div>

            <div className="deal-actions">
              <button
                className="btn btn-secondary deal-action"
                onClick={() => handleAddToShoppingList(deal)}
                disabled={addingToCart.has(String(deal.id))}
              >
                {addingToCart.has(String(deal.id)) ? 'Adding...' : 'Add to List'}
              </button>
              <button
                className="btn btn-primary deal-action"
                onClick={() => handleSearchRecipes(deal)}
              >
                Find Recipes
              </button>
            </div>
          </div>
        ))}
      </div>

      {deals.length === 0 && (
        <p className="no-deals">No deals available at the moment.</p>
      )}

      {deals.length > 0 && visibleDeals.length === 0 && (
        <p className="no-deals">No deals match the selected filters.</p>
      )}
    </div>
  );
};

export default DealsTab;
